import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useSupportWidget } from '../../contexts/SupportWidgetContext';
import config from '../../config/env';
import { SUPPORT_INBOX_EMAIL } from '../../constants/siteNav';
import { BRAND_LOGO_SRC, BRAND_NAME } from '../../constants/brand';

const TOPICS = [
  'Buying tickets',
  'Account & sign in',
  'Listing an event',
  'Payments & M-Pesa',
  'Refunds & cancellations',
  'Other'
];

const SupportEmailWidget = () => {
  const {
    emailWidgetOpen,
    emailWidgetMinimized,
    closeEmailWidget,
    minimizeEmailWidget,
    restoreEmailWidget
  } = useSupportWidget();

  const [form, setForm] = useState({ name: '', email: '', topic: TOPICS[0], message: '' });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!emailWidgetOpen) return null;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      setError('Please fill in all fields. Message must be at least 10 characters.');
      return;
    }

    setSending(true);
    setError('');

    try {
      const apiBase = (config.apiUrl || 'http://localhost:5000').replace(/\/$/, '');
      const response = await fetch(`${apiBase}/api/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          topic: form.topic,
          subject: form.topic,
          message: form.message.trim()
        })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setSubmitted(true);
      } else {
        setError(data.message || 'Failed to send message. Please try again.');
      }
    } catch {
      setError('Unable to reach the server. Please try WhatsApp or email us directly.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    closeEmailWidget();
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: '', email: '', topic: TOPICS[0], message: '' });
      setError('');
    }, 300);
  };

  const widget = emailWidgetMinimized ? (
    <button
      type="button"
      onClick={restoreEmailWidget}
      className="fixed bottom-6 left-6 z-[550] flex items-center gap-2.5 bg-white border border-gray-200 rounded-full pl-4 pr-5 py-2.5 shadow-xl hover:shadow-2xl transition-all text-sm font-semibold text-gray-800"
      aria-label="Restore support chat"
    >
      <span className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
        <i className="fas fa-headset text-white text-xs" />
      </span>
      Support
    </button>
  ) : (
    <div
      className="fixed bottom-6 left-6 z-[550] w-[min(100vw-2rem,380px)] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-gray-200/80 flex flex-col overflow-hidden animate-slide-up"
      role="dialog"
      aria-label="Support chat"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
        <div>
          <p className="text-[15px] font-bold text-gray-900 leading-tight">Support</p>
          <p className="text-[11px] text-gray-400 flex items-center gap-1.5 mt-0.5">
            <img src={BRAND_LOGO_SRC} alt="" className="h-3.5 w-auto rounded-sm object-contain" />
            Powered by {BRAND_NAME}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={minimizeEmailWidget}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            aria-label="Minimize"
          >
            <i className="fas fa-chevron-down text-xs" />
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            aria-label="Close"
          >
            <i className="fas fa-times text-sm" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto max-h-[min(70vh,520px)]">
        {submitted ? (
          <div className="px-5 py-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-check text-emerald-500 text-xl" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">Message sent!</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-1">
              Thanks for reaching out. Our team will get back to you within minutes to an hour at{' '}
              <strong className="text-gray-800">{form.email}</strong>.
            </p>
            <p className="text-xs text-gray-400 mt-4">
              Need faster help?{' '}
              <Link to="/contact" className="text-orange-600 hover:underline" onClick={handleClose}>
                Try WhatsApp
              </Link>
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-5 text-sm font-semibold text-orange-600 hover:text-orange-700"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="px-4 py-4 bg-gray-50/80 border-b border-gray-100">
              <p className="text-[11px] text-gray-400 mb-1">
                Niko Vibe · {new Date().toLocaleTimeString('en-KE', { hour: 'numeric', minute: '2-digit' })}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                Thanks! What can we help you with today? Fill in the form below and we&apos;ll email you back at{' '}
                <span className="font-medium">{SUPPORT_INBOX_EMAIL}</span>.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              <div>
                <label htmlFor="support-name" className="sr-only">Your name</label>
                <input
                  id="support-name"
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={handleChange('name')}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="support-email" className="sr-only">Your email</label>
                <input
                  id="support-email"
                  type="email"
                  placeholder="Your email address"
                  value={form.email}
                  onChange={handleChange('email')}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="support-topic" className="sr-only">Topic</label>
                <select
                  id="support-topic"
                  value={form.topic}
                  onChange={handleChange('topic')}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 bg-white transition-colors"
                >
                  {TOPICS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="support-message" className="sr-only">Message</label>
                <textarea
                  id="support-message"
                  rows={4}
                  placeholder="Describe your question or issue…"
                  value={form.message}
                  onChange={handleChange('message')}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 resize-none transition-colors"
                />
              </div>

              {error && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
              )}

              <button
                type="submit"
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
              >
                {sending ? (
                  <>
                    <i className="fas fa-spinner fa-spin text-sm" />
                    Sending…
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane text-sm" />
                    Send message
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>

      {!submitted && (
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
          <p className="text-[10px] text-gray-400 leading-relaxed text-center">
            By contacting us, you agree to our{' '}
            <Link to="/terms-of-service" className="underline hover:text-gray-600">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy-policy" className="underline hover:text-gray-600">Privacy Policy</Link>.
          </p>
        </div>
      )}
    </div>
  );

  return createPortal(widget, document.body);
};

export default SupportEmailWidget;
